import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import MiniConsultModal from '../components/MiniConsultModal';
import BookingModal from '../components/BookingModal';
import NextAvailableSlot from '../components/NextAvailableSlot';
import ReviewsCarousel from '../components/ReviewsCarousel';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import ServicesGrid from '../components/ServicesGrid';
import KnowledgeBaseSection from '../components/KnowledgeBaseSection';
import FAQSection from '../components/FAQSection';
import LazyImage from '../components/LazyImage';
import CaseGrid from '../components/gallery/CaseGrid';
import { SiteLinkColumns, AreaTreatmentLinks } from '../components/SiteFooter';
import {
  initializeTracking,
  trackPhoneClick,
  trackWhatsAppClick,
  trackSmileSelfieOpen,
  trackSmileSelfieCapture,
  trackVoiceNoteOpen,
  trackVoiceNoteRecord,
  trackBookingClick,
  trackServiceView
} from '../utils/analytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { homePageSEO, PUBLIC_SITE_URL } from '../data/seo';
import { 
  getOrganizationLD, 
  getDentistClinicLD, 
  getPersonClinicianLD,
  getWebSiteLD,
  getSpeakableLD,
  getFAQPageLD
} from '../data/structuredData';
import { homepageFAQs } from '../data/faqs';
import { 
  Phone, 
  MapPin, 
  Clock, 
  Mail,
  ChevronDown,
  Smile,
  Sparkles,
  Heart,
  Shield,
  Award,
  Stethoscope,
  CheckCircle2,
  ArrowRight,
  Camera,
  X,
  RotateCcw,
  Send,
  Mic,
  Square,
  Play,
  Pause,
  Instagram,
  Facebook,
  Calendar,
  Twitter,
  Music2
} from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

// Custom hook for time-based theme
const useTimeBasedTheme = () => {
  const [timePeriod, setTimePeriod] = useState('afternoon');

  useEffect(() => {
    const updateTheme = () => {
      const hour = new Date().getHours();
      
      if (hour >= 6 && hour < 12) {
        setTimePeriod('morning');
      } else if (hour >= 12 && hour < 18) {
        setTimePeriod('afternoon');
      } else if (hour >= 18 && hour < 22) {
        setTimePeriod('evening');
      } else {
        setTimePeriod('night');
      }
    };

    updateTheme();
    // Update theme every minute to catch transitions
    const interval = setInterval(updateTheme, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Define color palettes for each time period
  const colorPalettes = {
    morning: {
      // Lighter, energetic - sunrise colors
      invertedBlue: '30 95% 60%',      // Bright sunrise orange
      invertedRed: '195 85% 65%',       // Light cyan/sky blue
      invertedGreen: '320 55% 75%',     // Soft pink/magenta
      invertedYellow: '215 95% 65%',    // Bright blue
      background: '45 100% 98%',        // Warm white
    },
    afternoon: {
      // Current vibrant palette
      invertedBlue: '26 91% 39%',       // Orange/Gold
      invertedRed: '185 81% 50%',       // Cyan
      invertedGreen: '315 48% 63%',     // Magenta
      invertedYellow: '225 98% 50%',    // Deep Blue
      background: '0 0% 100%',          // Pure white
    },
    evening: {
      // Warmer, calmer - sunset tones
      invertedBlue: '20 85% 45%',       // Deep warm orange
      invertedRed: '280 40% 55%',       // Warm purple
      invertedGreen: '340 60% 60%',     // Coral/salmon
      invertedYellow: '240 60% 40%',    // Muted deep blue
      background: '35 30% 96%',         // Warm off-white
    },
    night: {
      // Darker, muted - night mode
      invertedBlue: '25 60% 35%',       // Muted burnt orange
      invertedRed: '220 40% 45%',       // Muted blue
      invertedGreen: '310 35% 50%',     // Muted purple
      invertedYellow: '230 50% 35%',    // Dark blue
      background: '230 15% 15%',        // Dark background
    }
  };

  return { timePeriod, palette: colorPalettes[timePeriod] };
};

const Home = () => {
  const [showAllServices, setShowAllServices] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasShownExitIntent, setHasShownExitIntent] = useState(false);
  const [interestedTreatment, setInterestedTreatment] = useState(null);
  const [lastViewedSection, setLastViewedSection] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [businessRating, setBusinessRating] = useState(4.9); // Updated to actual Google rating
  const [totalReviews, setTotalReviews] = useState(26);
  const [googleMapsUri, setGoogleMapsUri] = useState('');
  const [flippedService, setFlippedService] = useState(null);
  
  // Voice recording states
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceError, setVoiceError] = useState('');
  const [isTriangleExpanded, setIsTriangleExpanded] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showMiniConsult, setShowMiniConsult] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  
  // Cookie consent states
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);
  
  // Time-based theme
  const { timePeriod, palette } = useTimeBasedTheme();
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  const whatsappNumber = '447787814064'; // Format: country code + number without spaces
  const whatsappMessage = encodeURIComponent('Hello! I would like to book an appointment at restore.dental');
  
  // Use different WhatsApp link based on device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile 
    ? `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}`;
    
  const phoneNumber = '07787814064';
  const phoneLink = `tel:${phoneNumber}`;
  
  // Google My Business link
  const googleBusinessLink = 'https://www.google.com/maps/place/Restore.Dental+%7C+Dr.+Kartik+Patel+%7C+West+London+Invisalign/@51.514787,-0.275029,15z/data=!4m6!3m5!1s0x48760fbb2777c9ad:0xccd55a940edf434d!8m2!3d51.514787!4d-0.275029!16s%2Fg%2F11j62n_f9b';

  const services = [
    { name: 'Invisalign', icon: Smile, description: 'Straighten your teeth discreetly', videoId: '', pageLink: '/invisalign-acton' },
    { name: 'Composite Bonding', icon: Sparkles, description: 'Life-changing smile in one visit', videoId: '', pageLink: '/composite-bonding-acton' },
    { name: 'Emergency Dentist', icon: Heart, description: 'Same-day emergency appointments', videoId: '', pageLink: '/emergency-dentist-acton' },
    { name: 'General Dentistry', icon: Stethoscope, description: 'Complete family dental care', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Cosmetic Dentistry', icon: Sparkles, description: 'Transform your smile', videoId: '', pageLink: '/cosmetic-dentist-acton' },
    { name: 'Teeth Whitening', icon: Sparkles, description: 'Brighten your smile professionally', videoId: '', pageLink: '/cosmetic-dentist-acton' },
    { name: 'Porcelain Veneers', icon: Award, description: 'The gold standard in smile makeovers', videoId: '', pageLink: '/cosmetic-dentist-acton' },
    { name: 'Dental Implants', icon: Award, description: 'Permanent tooth replacement', videoId: '', pageLink: '/dentist-acton' },
    { name: 'White Fillings', icon: Shield, description: 'Natural-looking restorations', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Aesthetic Bridges', icon: Award, description: 'Highly aesthetic tooth replacement', videoId: '', pageLink: '/cosmetic-dentist-acton' },
    { name: 'Aesthetic Dentures', icon: Smile, description: 'Natural-looking, comfortable dentures', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Restorative Dentistry', icon: Stethoscope, description: 'Long-lasting repairs & rebuilds', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Pain Management', icon: Heart, description: 'Getting patients out of pain quickly', videoId: '', pageLink: '/emergency-dentist-acton' },
    { name: 'Teeth Grinding Treatment', icon: Shield, description: 'Protecting broken teeth', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Sleep Apnea Management', icon: Stethoscope, description: 'Comprehensive sleep care', videoId: '', pageLink: '/dentist-acton' },
    { name: 'TMD Treatment', icon: Shield, description: 'Jaw pain relief', videoId: '', pageLink: '/dentist-acton' },
    { name: 'Sedation Dentistry', icon: Heart, description: 'Great with anxious patients', videoId: '', pageLink: '/dentist-acton' }
  ];

  const displayedServices = showAllServices ? services : services.slice(0, 6);

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBooking = async (serviceName = '') => {
    // Track booking click, then go to the paid booking flow
    trackBookingClick();
    window.location.href = '/book';
  };
  
  const handleBookingWithForm = (serviceName = '') => {
    window.location.href = '/book';
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' },
        audio: false 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraStream(stream);
      }
    } catch (error) {
      console.error('Camera access error:', error);
      setCameraError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/png');
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const sendToWhatsApp = () => {
    // Download the image
    if (capturedImage) {
      const link = document.createElement('a');
      link.href = capturedImage;
      link.download = 'my-smile-restore-dental.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Open WhatsApp with message
      const message = encodeURIComponent('Hi! I\'d like to discuss my smile transformation. I\'ve just taken a photo of my current smile that I\'ll attach to this conversation.');
      window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
      
      // Close camera dialog
      setShowCamera(false);
      setCapturedImage(null);
    }
  };

  const handleCameraOpen = () => {
    setShowCamera(true);
    setTimeout(() => {
      startCamera();
    }, 300);
  };

  const handleCameraClose = () => {
    stopCamera();
    setShowCamera(false);
    setCapturedImage(null);
    setCameraError('');
  };

  // Voice recording functions
  const startVoiceRecording = async () => {
    try {
      setVoiceError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        setAudioBlob(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      setVoiceError('Unable to access microphone. Please ensure microphone permissions are granted.');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const deleteVoiceRecording = () => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    setRecordedAudio(null);
    setAudioBlob(null);
    setRecordingTime(0);
    setVoiceError('');
  };

  const sendVoiceNote = () => {
    if (audioBlob) {
      // Download the audio file
      const link = document.createElement('a');
      link.href = recordedAudio;
      link.download = `voice-note-restore-dental-${Date.now()}.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Open WhatsApp with message
      const message = encodeURIComponent('Hi! I\'d like to leave you a voice note about my dental needs. I\'ve just recorded a message that I\'ll attach to this conversation.');
      window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
      
      // Close recorder dialog
      setTimeout(() => {
        setShowVoiceRecorder(false);
        deleteVoiceRecording();
      }, 1000);
    }
  };

  const handleVoiceRecorderOpen = () => {
    setShowVoiceRecorder(true);
    setVoiceError('');
  };

  const handleVoiceRecorderClose = () => {
    if (isRecording) {
      stopVoiceRecording();
    }
    setShowVoiceRecorder(false);
    deleteVoiceRecording();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Apply time-based color palette
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--inverted-blue', palette.invertedBlue);
    root.style.setProperty('--inverted-red', palette.invertedRed);
    root.style.setProperty('--inverted-green', palette.invertedGreen);
    root.style.setProperty('--inverted-yellow', palette.invertedYellow);
    
    // For night mode, also adjust text colors for better readability
    if (timePeriod === 'night') {
      root.style.setProperty('--background', palette.background);
      root.style.setProperty('--foreground', '0 0% 95%'); // Light text for dark bg
      document.body.style.backgroundColor = `hsl(${palette.background})`;
      document.body.style.color = 'hsl(0 0% 95%)';
    } else {
      root.style.setProperty('--background', palette.background);
      root.style.setProperty('--foreground', '0 0% 3.9%'); // Dark text for light bg
      document.body.style.backgroundColor = `hsl(${palette.background})`;
      document.body.style.color = 'hsl(0 0% 3.9%)';
    }
  }, [palette, timePeriod]);

  // Check cookie consent on load
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      const consentData = JSON.parse(consent);
      setCookieConsent(consentData);
      setShowCookieBanner(false);
      
      // Initialize tracking based on consent
      initializeTracking(consentData);
    } else {
      // Show banner after a short delay
      setTimeout(() => setShowCookieBanner(true), 2000);
    }
  }, []);

  // Cookie consent functions
  const acceptAllCookies = () => {
    const consent = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setCookieConsent(consent);
    setShowCookieBanner(false);
    
    // Initialize all tracking
    initializeTracking(consent);
  };

  const rejectNonEssential = () => {
    const consent = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setCookieConsent(consent);
    setShowCookieBanner(false);
  };

  const saveCustomPreferences = (preferences) => {
    const consent = {
      ...preferences,
      essential: true, // Always true
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setCookieConsent(consent);
    setShowCookieSettings(false);
    setShowCookieBanner(false);
    
    // Initialize tracking based on preferences
    initializeTracking(consent);
  };

  // Fetch Google Reviews & Rating
  useEffect(() => {
    const fetchGoogleRating = async () => {
      try {
        setReviewsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-rating`);
        if (!response.ok) throw new Error('Failed to fetch Google rating');
        
        const data = await response.json();
        setBusinessRating(data.rating || 4.9);
        setTotalReviews(data.user_rating_count || 26);
        setGoogleMapsUri(data.google_maps_uri || '');
        setReviews(data.reviews || []);
      } catch (error) {
        console.error('Error fetching Google rating:', error);
        // Keep fallback data
        setBusinessRating(4.9);
        setTotalReviews(26);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchGoogleRating();
  }, []);

  // Track which treatment user is interested in
  const trackTreatmentInterest = (treatmentName) => {
    setInterestedTreatment(treatmentName);
    console.log('User interested in:', treatmentName);
  };

  // Track section views
  const trackSectionView = (sectionName) => {
    setLastViewedSection(sectionName);
  };

  // Interactive slider functionality
  useEffect(() => {
    const slider = document.getElementById('slider-handle');
    const afterSection = document.getElementById('after-section');
    const container = slider?.parentElement;

    if (!slider || !afterSection || !container) return;

    let isDragging = false;

    const updateSlider = (clientX) => {
      const rect = container.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;
      
      slider.style.left = `${percentage}%`;
      afterSection.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
    };

    const handleMouseDown = () => {
      isDragging = true;
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      updateSlider(e.clientX);
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      updateSlider(e.touches[0].clientX);
    };

    const handleEnd = () => {
      isDragging = false;
    };

    slider.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    
    slider.addEventListener('touchstart', handleMouseDown);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      slider.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, []);


  // Sticky booking bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownExitIntent) {
        setShowExitIntent(true);
        setHasShownExitIntent(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownExitIntent]);

  // Get personalized exit offer based on user behavior
  const getPersonalizedOffer = () => {
    const treatment = interestedTreatment || lastViewedSection;
    
    if (!treatment) {
      return {
        title: "Wait! Before You Go...",
        description: "Get your FREE smile consultation + £50 off your first treatment when you book this week!",
        treatment: null
      };
    }

    return {
      title: `Interested in ${treatment}?`,
      description: `Bring a friend who also wants ${treatment} and you'll BOTH get 10% off your treatments – if you both proceed and book within the next 7 days!`,
      treatment: treatment,
      highlight: "Bring a Friend, Save 10% Each"
    };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={homePageSEO.title}
        description={homePageSEO.description}
        path={homePageSEO.path}
        keywords={homePageSEO.keywords}
        image={homePageSEO.image}
        structuredData={[
          getOrganizationLD(),
          getDentistClinicLD({
            aggregateRating: {
              value: businessRating,
              count: totalReviews,
              best: 5
            }
          }),
          getPersonClinicianLD({
            name: 'Dr. Kartik Patel',
            honorificPrefix: 'Dr.',
            honorificSuffix: 'BDS',
            jobTitle: 'Principal Dentist',
            description: 'Award-winning dentist with 15+ years experience specializing in Invisalign, cosmetic dentistry, and dental implants.',
            image: `${PUBLIC_SITE_URL}/dr-kartik-patel.jpg`,
            medicalSpecialty: ['Cosmetic Dentistry', 'Invisalign', 'Restorative Dentistry'],
          }),
          getWebSiteLD(),
          getSpeakableLD([
            'Award-winning dentist in Acton',
            'Expert Invisalign provider',
            'Same-day emergency appointments',
            '0% interest-free finance available'
          ]),
          getFAQPageLD(homepageFAQs),
        ]}
      />
      
      {/* Special Offer Banner */}
      <div className="bg-gradient-to-r from-burnt-coral via-burnt-coral/90 to-sky-gray text-white py-3 text-center animate-pulse">
        <p className="text-sm md:text-base font-semibold">
          ⚡ Limited Slots This Week • New Patient Special: Free Consultation + £50 Off First Treatment
        </p>
      </div>

      {/* Sticky Booking Bar */}
      {showStickyBar && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg border-b-2 border-burnt-coral animate-slideDown">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smile className="w-6 h-6 text-burnt-coral" />
                <div>
                  <p className="font-bold text-rabbit text-sm md:text-base">restore.dental</p>
                  <p className="text-xs text-chocolate-malt hidden md:block">Same-day appointments available</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                  onClick={handleBooking}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Book Now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
                  asChild
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    <WhatsAppIcon className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Popup - Personalized */}
      {showExitIntent && (
        <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl animate-scaleIn">
            <button
              onClick={() => setShowExitIntent(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-rabbit"
              aria-label="Close special offer popup"
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-burnt-coral/10 rounded-full flex items-center justify-center mx-auto mb-4">
                {getPersonalizedOffer().treatment ? (
                  <Sparkles className="w-8 h-8 text-burnt-coral" />
                ) : (
                  <Smile className="w-8 h-8 text-burnt-coral" />
                )}
              </div>
              
              <h3 className="text-2xl font-bold text-rabbit mb-3">
                {getPersonalizedOffer().title}
              </h3>
              
              {getPersonalizedOffer().highlight && (
                <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-burnt-coral to-sky-gray text-white rounded-full animate-pulse">
                  <p className="text-sm font-bold">
                    {getPersonalizedOffer().highlight}
                  </p>
                </div>
              )}
              
              <p className="text-chocolate-malt mb-6">
                {getPersonalizedOffer().description}
              </p>

              {getPersonalizedOffer().treatment && (
                <div className="bg-sky-gray/10 rounded-lg p-4 mb-6">
                  <p className="text-sm text-chocolate-malt">
                    <strong>How it works:</strong><br/>
                    1. Book your {getPersonalizedOffer().treatment} consultation<br/>
                    2. Bring a friend who wants the same treatment<br/>
                    3. You both get 10% off if you both proceed<br/>
                    4. Must book within 7 days
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  size="lg"
                  className="w-full bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                  onClick={() => {
                    setShowExitIntent(false);
                    handleBooking();
                  }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {getPersonalizedOffer().treatment ? 'Book Now & Claim Offer' : 'Claim Your Free Consultation'}
                </Button>
                
                {/* Branded WhatsApp Button */}
                <Button
                  size="lg"
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-lg"
                  asChild
                  onClick={() => setShowExitIntent(false)}
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp Us About This Offer
                  </a>
                </Button>
              </div>

              <p className="text-xs text-chocolate-malt/70 mt-4">
                {getPersonalizedOffer().treatment ? 
                  '⏰ Offer valid for 7 days from booking' : 
                  '⏰ Offer ends this week • Limited slots available'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <Header 
        onBooking={handleBooking}
        onServicesScroll={scrollToServices}
        phoneLink={phoneLink}
        trackPhoneClick={trackPhoneClick}
      />

      {/* Hero Section */}
      <HeroSection 
        onBooking={handleBooking}
        onLearnMore={scrollToServices}
        businessRating={businessRating}
        totalReviews={totalReviews}
        whatsappLink={whatsappLink}
      />

      {/* Google Reviews Carousel */}
      <ReviewsCarousel />

      {/* Philosophy Section */}
      <section className="py-16 md:py-20 bg-amberlight/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Completely original opening */}
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Excellence in Dental Care, Delivered with Heart
              </h3>
              <p className="text-lg text-chocolate-malt leading-relaxed max-w-3xl mx-auto">
                At restore.dental, our relentless pursuit of perfection means you get care that's built to last. We believe in doing things properly – <strong>not rushing, not cutting corners, just exceptional dentistry that delivers natural-looking results and functions as nature intended.</strong>
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-12">
              <div>
                <LazyImage 
                  src="https://images.unsplash.com/photo-1684607632599-748b1792a116?w=800&q=75&fm=webp&auto=format&fit=crop"
                  srcSet="https://images.unsplash.com/photo-1684607632599-748b1792a116?w=480&q=75&fm=webp&auto=format&fit=crop 480w, https://images.unsplash.com/photo-1684607632599-748b1792a116?w=800&q=75&fm=webp&auto=format&fit=crop 800w, https://images.unsplash.com/photo-1684607632599-748b1792a116?w=1200&q=75&fm=webp&auto=format&fit=crop 1200w"
                  sizes="(min-width: 768px) 50vw, 100vw"
                  width="800"
                  height="1000"
                  alt="Happy patient smiling after dental treatment at Restore.Dental, Acton"
                  className="rounded-2xl shadow-2xl w-full h-auto"
                />
              </div>
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                  Trained Worldwide, Serving West London
                </h3>
                <p className="text-lg text-chocolate-malt mb-6 leading-relaxed">
                  Dr. Kartik Patel has invested significantly in advanced training with leading dentists across the globe. This commitment brings you access to cutting-edge techniques and approaches that deliver superior, long-lasting results.
                </p>
                <p className="text-lg text-chocolate-malt mb-6 leading-relaxed">
                  We've also invested in the best technology, equipment, and materials available. Why? Because your outcomes matter. Every treatment is approached with the same question: <strong>"What would I want for my own family?"</strong>
                </p>
                <p className="text-lg text-chocolate-malt mb-6 leading-relaxed">
                  Our focus is on creating results that look natural, function properly, and stand the test of time – achieved in the most comfortable way possible.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-burnt-coral/10 text-burnt-coral rounded-full text-sm font-medium">
                    Advanced Training
                  </span>
                  <span className="px-4 py-2 bg-sky-gray/10 text-sky-gray rounded-full text-sm font-medium">
                    Best Technology
                  </span>
                  <span className="px-4 py-2 bg-amberlight/30 text-rabbit rounded-full text-sm font-medium">
                    Natural Results
                  </span>
                </div>
              </div>
            </div>

            {/* Values statement */}
            <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral/20">
              <h4 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">
                Authenticity, Honesty, Acting in Your Best Interest
              </h4>
              <p className="text-lg text-chocolate-malt max-w-2xl mx-auto">
                These aren't just words – they're core to how we practice. You'll experience genuine care from a team that values building real relationships over quick transactions. Because your dental health deserves a partner you can trust for the long term.
              </p>
            </div>
          </div>
        </div>

      {/* Meet Your Dentist Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-rabbit mb-4">
                Your Dentist, Dr. Kartik Patel
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#01A9B4] to-[#D4AF37] mx-auto mb-6"></div>
              <p className="text-lg text-chocolate-malt max-w-2xl mx-auto">
                Meet the dentist dedicated to delivering exceptional care with authenticity and expertise
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Photo */}
              <div className="order-2 md:order-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#01A9B4]/20 to-[#D4AF37]/20 rounded-2xl transform rotate-3"></div>
                  <div className="relative rounded-2xl shadow-2xl overflow-hidden">
                    <img
                      src="/dr-kartik-patel.webp"
                      alt="Dr. Kartik Patel BDS (King's College London), principal dentist at Restore.Dental Acton"
                      width="720"
                      height="675"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border-2 border-[#01A9B4]/20">
                    <div className="flex items-center gap-3">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#01A9B4]">150+</p>
                        <p className="text-xs text-gray-600">Invisalign Cases</p>
                      </div>
                      <div className="w-px h-12 bg-gray-300"></div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#01A9B4]">15+</p>
                        <p className="text-xs text-gray-600">Years Experience</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="order-1 md:order-2">
                <div className="space-y-6">
                  <p className="text-lg text-chocolate-malt leading-relaxed">
                    Dr. Kartik Patel is the founder and principal dentist at restore.dental, where he brings together world-class training, cutting-edge technology, and a patient-first philosophy to deliver exceptional dental care.
                  </p>
                  
                  <p className="text-lg text-chocolate-malt leading-relaxed">
                    With over <strong>150 completed Invisalign cases</strong> and extensive training from leading dentists worldwide, Dr. Patel has built a reputation for creating natural-looking results that stand the test of time.
                  </p>

                  <div className="bg-gradient-to-r from-[#01A9B4]/10 to-[#D4AF37]/10 rounded-xl p-6 border-l-4 border-[#01A9B4]">
                    <p className="text-chocolate-malt italic">
                      "I approach every patient with one question: <strong>What would I want for my own family?</strong> This philosophy guides every decision we make, from the materials we choose to the time we invest in getting things right."
                    </p>
                    <p className="text-sm text-gray-600 mt-3">— Dr. Kartik Patel</p>
                  </div>

                  <p className="text-lg text-chocolate-malt leading-relaxed">
                    His commitment to excellence means never rushing, never cutting corners, and always prioritizing long-lasting results over quick fixes. Through his partnership with Perfect Smile, Dr. Patel combines clinical expertise with a genuine understanding that exceptional dentistry changes lives.
                  </p>

                  <div className="space-y-3 pt-4">
                    <h4 className="font-bold text-rabbit text-lg">Key Expertise:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">Invisalign Specialist</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">Cosmetic Dentistry</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">Dental Implants</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">Composite Bonding</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">Preventive Care</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#01A9B4]" />
                        <span className="text-sm text-chocolate-malt">TMJ Treatment</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button 
                      size="lg"
                      className="bg-[#01A9B4] hover:bg-[#006A71] text-white"
                      onClick={handleBooking}
                    >
                      Book a Consultation with Dr. Patel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
              Our Services
            </h3>
            <p className="text-lg text-chocolate-malt max-w-2xl mx-auto">
              Comprehensive dental care tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {displayedServices.map((service, index) => {
              const IconComponent = service.icon;
              const isFlipped = flippedService === index;
              
              return (
                <div 
                  key={index} 
                  className={`flip-card ${isFlipped ? 'flipped' : ''}`}
                  onClick={() => {
                    setFlippedService(isFlipped ? null : index);
                    trackServiceView(service.name);
                  }}
                >
                  <div className="flip-card-inner">
                    {/* Front of card */}
                    <div className="flip-card-front">
                      <Card className="border-2 border-transparent hover:border-burnt-coral transition-all duration-300 hover:shadow-xl cursor-pointer group h-full relative">
                        <CardContent className="p-6 pb-12 min-h-[340px] flex flex-col">
                          {/* Before/After Image Frame - Top Right Corner - Photo Ratio with Hover Zoom */}
                          {/* Only show for cosmetic/aesthetic services */}
                          {!['Emergency Dentist', 'General Dentistry', 'Restorative Dentistry', 'Pain Management', 'TMD Treatment', 'Sleep Apnea Management', 'Sedation Dentistry'].includes(service.name) && (
                            <div 
                              className="absolute top-4 right-4 w-28 h-20 rounded-lg overflow-hidden border-2 border-burnt-coral/30 bg-gradient-to-br from-burnt-coral/10 to-sky-gray/10 transition-all duration-300 hover:scale-[2] hover:z-[100] hover:shadow-2xl cursor-pointer"
                              style={{ aspectRatio: '4/3' }}
                              onMouseEnter={(e) => {
                                // Ensure parent card also gets high z-index
                                const card = e.currentTarget.closest('.flip-card');
                                if (card) card.style.zIndex = '100';
                              }}
                              onMouseLeave={(e) => {
                                // Reset parent card z-index
                                const card = e.currentTarget.closest('.flip-card');
                                if (card) card.style.zIndex = '';
                              }}
                            >
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-xs font-semibold text-burnt-coral">Before/After</div>
                                  <div className="text-[10px] text-chocolate-malt mt-1">Coming Soon</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="w-12 h-12 bg-burnt-coral/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-burnt-coral group-hover:scale-110 transition-all duration-300">
                            <IconComponent className="w-6 h-6 text-burnt-coral group-hover:text-white transition-colors duration-300" />
                          </div>
                          <h4 className="text-xl font-semibold text-rabbit mb-2">
                            {service.name}
                          </h4>
                          <p className={`text-chocolate-malt mb-4 ${!['Emergency Dentist', 'General Dentistry', 'Restorative Dentistry', 'Pain Management', 'TMD Treatment', 'Sleep Apnea Management', 'Sedation Dentistry'].includes(service.name) ? 'pr-32' : ''}`}>
                            {service.description}
                          </p>
                          
                          {/* CTA Section - Redesigned with spacing - pushed to bottom */}
                          <div className="mt-auto space-y-3">
                            {/* Next Available Display */}
                            <NextAvailableSlot serviceName={service.name} />
                            
                            {/* "Book Appointment Now" text with icon buttons */}
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-sm font-semibold text-rabbit">Book Appointment Now:</span>
                              
                              {/* Phone Icon Button - Opens Dentally */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBooking(service.name);
                                }}
                                className="w-10 h-10 bg-burnt-coral hover:bg-burnt-coral/90 text-white rounded-lg flex items-center justify-center transition-all hover:scale-110"
                                aria-label={`Book ${service.name} appointment by phone`}
                                title="Quick book via Dentally"
                              >
                                <Phone className="w-5 h-5" aria-hidden="true" />
                              </button>
                              
                              {/* Calendar Icon Button - Opens Booking Form */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBookingWithForm(service.name);
                                }}
                                className="w-10 h-10 bg-sky-gray hover:bg-sky-gray/80 text-white rounded-lg flex items-center justify-center transition-all hover:scale-110"
                                aria-label={`Book ${service.name} using online form`}
                                title="Book with our form"
                              >
                                <Calendar className="w-5 h-5" aria-hidden="true" />
                              </button>
                              
                              {/* WhatsApp Icon Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(whatsappLink, '_blank');
                                }}
                                className="w-10 h-10 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg flex items-center justify-center transition-all hover:scale-110"
                                aria-label={`Contact us about ${service.name} on WhatsApp`}
                                title="Chat on WhatsApp"
                              >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                              </button>
                            </div>
                            
                            {/* Learn More Button - with spacing above */}
                            {service.pageLink && (
                              <Link to={service.pageLink}>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="w-full bg-white border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Learn More →
                                </Button>
                              </Link>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Back of card */}
                    <div className="flip-card-back">
                      <Card className="border-2 border-burnt-coral bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 cursor-pointer h-full">
                        <CardContent className="p-4 h-full flex flex-col">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-lg font-semibold text-rabbit">
                              {service.name}
                            </h4>
                            <button 
                              className="text-burnt-coral hover:text-burnt-coral/70"
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Close ${service.name} information card`}
                            >
                              <X className="w-5 h-5" aria-hidden="true" />
                            </button>
                          </div>
                          
                          {service.videoId ? (
                            <div className="flex-1 rounded-lg overflow-hidden">
                              <iframe
                                width="100%"
                                height="100%"
                                src={`https://www.youtube.com/embed/${service.videoId}`}
                                title={service.name}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="min-h-[200px]"
                              ></iframe>
                            </div>
                          ) : (
                            <div className="flex-1 flex items-center justify-center bg-gray-100 rounded-lg">
                              <p className="text-chocolate-malt text-center px-4">
                                Video coming soon!<br/>
                                <span className="text-sm">Check back later for examples</span>
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!showAllServices && (
            <div className="text-center mt-8">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setShowAllServices(true)}
                className="border-2 border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
              >
                View All Services
                <ChevronDown className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-amberlight/10 via-white to-sky-gray/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Your Journey With Us
              </h3>
              <p className="text-lg text-chocolate-malt">
                A thoughtful, comprehensive process designed around you
              </p>
            </div>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-burnt-coral rounded-full flex items-center justify-center text-white font-bold text-xl">
                  1
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-semibold text-rabbit mb-2">
                    Open Conversation, Not a Sales Pitch
                  </h4>
                  <p className="text-chocolate-malt">
                    We start by getting to know <strong>you</strong> – your goals, your motivations, your concerns. What does your ideal smile look like? What's holding you back? This isn't about selling you treatment; it's about understanding what success means to you.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-sky-gray rounded-full flex items-center justify-center text-white font-bold text-xl">
                  2
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-semibold text-rabbit mb-2">
                    Comprehensive Assessment
                  </h4>
                  <p className="text-chocolate-malt">
                    We take professional photographs, digital x-rays, and 3D scans. We're looking at the whole picture – not just your teeth, but your bite, jaw function, gum health, and facial aesthetics. Then we discuss our findings openly with you.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-burnt-coral rounded-full flex items-center justify-center text-white font-bold text-xl">
                  3
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-semibold text-rabbit mb-2">
                    Detailed Proposal (Via WhatsApp)
                  </h4>
                  <p className="text-chocolate-malt mb-3">
                    We don't rush you into decisions. Instead, we take time to create a comprehensive proposal sent via WhatsApp that includes:
                  </p>
                  <ul className="space-y-2 text-chocolate-malt">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-burnt-coral flex-shrink-0 mt-0.5" />
                      <span><strong>Problems identified</strong> – what we found and why it matters</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-sky-gray flex-shrink-0 mt-0.5" />
                      <span><strong>Solutions proposed</strong> – your options explained clearly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-burnt-coral flex-shrink-0 mt-0.5" />
                      <span><strong>Transparent costs</strong> – no hidden fees, ever</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-sky-gray flex-shrink-0 mt-0.5" />
                      <span><strong>Procedure videos</strong> – see exactly what to expect</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-burnt-coral flex-shrink-0 mt-0.5" />
                      <span><strong>Our policies</strong> – guarantees, aftercare, everything upfront</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-sky-gray rounded-full flex items-center justify-center text-white font-bold text-xl">
                  4
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-semibold text-rabbit mb-2">
                    Your Direct Line to Dr. Patel
                  </h4>
                  <p className="text-chocolate-malt">
                    Once you're our patient, you have a <strong>direct WhatsApp line</strong> to discuss anything – your proposal, ongoing treatment, or future concerns. Have a question at 10pm? Send it. We'll respond. <strong>"Once a patient, always a patient"</strong> isn't just a slogan – it's how we practice.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-burnt-coral rounded-full flex items-center justify-center text-white font-bold text-xl">
                  5
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg border-2 border-burnt-coral">
                  <h4 className="text-xl font-semibold text-rabbit mb-2">
                    Long-Term Partnership
                  </h4>
                  <p className="text-chocolate-malt">
                    We're not interested in one-off transactions. We want to be your dental home for life – looking after you as your needs evolve, preventing problems before they start, and being there when you need us. That's the restore.dental difference.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center p-8 bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5 rounded-2xl border-2 border-burnt-coral/20">
              <p className="text-lg text-rabbit font-semibold mb-4">
                Ready to experience dentistry done differently?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                  onClick={handleBooking}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Book Your Free Consultation
                </Button>
                <Button
                  size="lg"
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
                  asChild
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Ask Questions First
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="py-16 md:py-20 bg-sky-gray/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
              What Makes Us Different?
            </h3>
            <p className="text-lg text-chocolate-malt mb-8">
              We're not just another dental practice. Here's why patients choose restore.dental
            </p>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  size="lg"
                  className="bg-burnt-coral hover:bg-burnt-coral/90 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => trackSectionView('What Makes Us Different')}
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Discover What Sets Us Apart
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl md:text-3xl font-bold text-rabbit mb-4">
                    What Makes restore.dental Different
                  </DialogTitle>
                  <DialogDescription className="text-base text-chocolate-malt">
                    Unlike typical dental practices, we offer something truly unique
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-6">
                  <div className="flex gap-3 items-start p-4 bg-burnt-coral/5 rounded-lg hover:bg-burnt-coral/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">World-Class Training & Expertise</h4>
                      <p className="text-sm text-chocolate-malt">
                        Dr. Patel has trained extensively with leading dentists worldwide, bringing advanced techniques that many UK practices don't offer. You're getting expertise that goes far beyond standard dental training.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-start p-4 bg-sky-gray/5 rounded-lg hover:bg-sky-gray/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-sky-gray flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">Premium Materials & Technology</h4>
                      <p className="text-sm text-chocolate-malt">
                        We use only the finest equipment and materials available. Not because we have to, but because your results matter. Digital scanners, advanced imaging, materials chosen for longevity.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-start p-4 bg-burnt-coral/5 rounded-lg hover:bg-burnt-coral/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">Built to Last, Done Right</h4>
                      <p className="text-sm text-chocolate-malt">
                        We focus on lasting solutions, not quick fixes. Natural-looking results that function properly and stand the test of time. We're obsessed with getting it right – the first time.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-start p-4 bg-sky-gray/5 rounded-lg hover:bg-sky-gray/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-sky-gray flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">Your Goals Guide Us</h4>
                      <p className="text-sm text-chocolate-malt">
                        We start by understanding YOU – your goals, concerns, and what success looks like for you. Then comprehensive assessments (photos, x-rays, digital scans) to create your personalized plan.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-start p-4 bg-burnt-coral/5 rounded-lg hover:bg-burnt-coral/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">Transparent WhatsApp Communication</h4>
                      <p className="text-sm text-chocolate-malt">
                        After consultations, we send detailed proposals via WhatsApp: problems, solutions, costs, procedure videos, policies. Review at your pace. Ask questions anytime. Zero pressure.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-start p-4 bg-sky-gray/5 rounded-lg hover:bg-sky-gray/10 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-sky-gray flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-rabbit mb-1">Your Dental Partner for Life</h4>
                      <p className="text-sm text-chocolate-malt">
                        Direct WhatsApp access to Dr. Patel for questions, concerns, or advice – whenever you need it. We build lasting relationships because your dental health is an ongoing journey. Once our patient, you always have support.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-center text-chocolate-malt mb-4">
                    Ready to experience the difference?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                      onClick={handleBooking}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Book Appointment
                    </Button>
                    <Button
                      variant="outline"
                      className="border-2 border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
                      asChild
                    >
                      <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                        <WhatsAppIcon className="w-4 h-4 mr-2" />
                        WhatsApp Us
                      </a>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Before & After Gallery Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
              Real Results from Real Patients
            </h3>
            <p className="text-lg text-chocolate-malt max-w-2xl mx-auto mb-2">
              See the transformations we've achieved with our gentle, modern approach
            </p>
            <p className="text-sm text-chocolate-malt/70 italic">
              All images shown with patient consent • Results may vary
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <CaseGrid limit={3} onCaseClick={trackTreatmentInterest} />

            <div className="text-center mt-8">
              <Link to="/before-after" className="inline-flex items-center gap-1 text-burnt-coral font-semibold hover:underline mb-4" data-testid="home-gallery-link">
                View the full before &amp; after gallery <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <p className="text-chocolate-malt mb-4">
                Every image is a real Restore.Dental patient, published with written consent. See more cases at your consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                  onClick={handleBooking}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Book a Consultation
                </Button>
                <Button
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
                  asChild
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Ask About Results
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-sky-gray/5 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.5 16.364V23.91L26.68 27.41L25.59 29.228L18.41 25.09V16.364H20.5ZM28 20C28 24.418 24.418 28 20 28C15.582 28 12 24.418 12 20C12 15.582 15.582 12 20 12C24.418 12 28 15.582 28 20Z" fill="#4285F4"/>
                <path d="M20 8C13.373 8 8 13.373 8 20C8 26.627 13.373 32 20 32C26.627 32 32 26.627 32 20C32 13.373 26.627 8 20 8ZM20 30C14.486 30 10 25.514 10 20C10 14.486 14.486 10 20 10C25.514 10 30 14.486 30 20C30 25.514 25.514 30 20 30Z" fill="#EA4335"/>
                <path d="M20 0C8.955 0 0 8.955 0 20C0 31.045 8.955 40 20 40C31.045 40 40 31.045 40 20C40 8.955 31.045 0 20 0ZM20 36C11.163 36 4 28.837 4 20C4 11.163 11.163 4 20 4C28.837 4 36 11.163 36 20C36 28.837 28.837 36 20 36Z" fill="#FBBC04"/>
              </svg>
              <h3 className="text-3xl md:text-4xl font-bold text-rabbit">
                Google Reviews
              </h3>
            </div>
            <p className="text-lg text-chocolate-malt mb-4">
              Don't just take our word for it - see what our patients say
            </p>
            <a 
              href={googleBusinessLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-burnt-coral hover:text-burnt-coral/80 font-semibold"
            >
              <span>View all reviews on Google</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
                <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
              </svg>
            </a>
          </div>

          {/* Google Review Cards */}
          {reviewsLoading ? (
            <div className="text-center py-12">
              <p className="text-chocolate-malt">Loading reviews...</p>
            </div>
          ) : (
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
            <div key={review.time} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.profile_photo_url ? (
                    <img 
                      src={review.profile_photo_url} 
                      alt={review.author_name}
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-burnt-coral/10 flex items-center justify-center text-burnt-coral font-bold text-lg">
                      {review.author_name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-rabbit">{review.author_name}</p>
                    <p className="text-xs text-chocolate-malt/70">Google User</p>
                  </div>
                </div>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 0C4.477 0 0 4.477 0 10C0 15.523 4.477 20 10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0Z" fill="#4285F4"/>
                  <path d="M14.5 8.5L13 7L10 10L7 7L5.5 8.5L8.5 11.5L5.5 14.5L7 16L10 13L13 16L14.5 14.5L11.5 11.5L14.5 8.5Z" fill="white"/>
                </svg>
              </div>
              
              <div className="flex gap-1 mb-3">
                {[...Array(review.rating)].map((_, i) => (
                  <svg key={i} width="16" height="16" viewBox="0 0 16 16" fill="#FBBC04">
                    <path d="M8 0L10.163 5.256L16 6.11L12 10.27L12.944 16L8 13.256L3.056 16L4 10.27L0 6.11L5.837 5.256L8 0Z"/>
                  </svg>
                ))}
              </div>

              <p className="text-chocolate-malt text-sm mb-4 leading-relaxed">
                "{review.text}"
              </p>

              <div className="flex items-center justify-between text-xs text-chocolate-malt/70 pt-4 border-t border-gray-100">
                <a 
                  href={review.author_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-burnt-coral transition-colors"
                >
                  Posted on Google
                </a>
                <span>{review.relative_time_description}</span>
              </div>
            </div>
            ))}
          </div>
          )}

          {/* Google Badge */}
          <div className="text-center mt-12">
            <a 
              href={googleBusinessLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC04"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="text-left">
                <p className="text-sm font-semibold text-rabbit">restore.dental</p>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((star) => (
                      <svg key={star} width="12" height="12" viewBox="0 0 16 16" fill="#FBBC04">
                        <path d="M8 0L10.163 5.256L16 6.11L12 10.27L12.944 16L8 13.256L3.056 16L4 10.27L0 6.11L5.837 5.256L8 0Z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="text-xs text-chocolate-malt">5.0 • 27 reviews</span>
                </div>
              </div>
            </a>
            <p className="text-xs text-chocolate-malt/70 mt-4">
              These reviews are sourced directly from our Google Business Profile
            </p>
          </div>
        </div>
      </section>

      {/* Knowledge Base CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-sky-gray/10 via-burnt-coral/5 to-amberlight/10 border-y-2 border-burnt-coral/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-burnt-coral/20">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-burnt-coral to-sky-gray rounded-full mb-4">
                  <svg 
                    className="w-8 h-8 text-white" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                  Knowledge Base: Learn Here
                </h3>
                <p className="text-lg text-chocolate-malt mb-6 leading-relaxed">
                  Explore our comprehensive library of evidence-based dental articles. From treatment options to oral health tips, get expert insights written by Dr. Kartik Patel.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-gradient-to-br from-burnt-coral/5 to-transparent rounded-lg">
                  <div className="text-3xl font-bold text-burnt-coral mb-2">50+</div>
                  <p className="text-sm text-chocolate-malt">Expert Articles</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-sky-gray/5 to-transparent rounded-lg">
                  <div className="text-3xl font-bold text-sky-gray mb-2">10+</div>
                  <p className="text-sm text-chocolate-malt">Treatment Guides</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-amberlight/5 to-transparent rounded-lg">
                  <div className="text-3xl font-bold text-rabbit mb-2">100%</div>
                  <p className="text-sm text-chocolate-malt">Evidence-Based</p>
                </div>
              </div>

              <div className="text-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-burnt-coral to-sky-gray hover:from-burnt-coral/90 hover:to-sky-gray/90 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  asChild
                >
                  <Link to="/knowledge-base" className="inline-flex items-center">
                    <svg 
                      className="w-5 h-5 mr-2" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Browse Knowledge Base
                  </Link>
                </Button>
                <p className="text-sm text-chocolate-malt mt-4">
                  Updated regularly with the latest research and insights
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Common Questions
              </h3>
              <p className="text-lg text-chocolate-malt">
                Everything you need to know before your visit
              </p>
            </div>

            <div className="space-y-4">
              {/* FAQ 1 */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
                  className="w-full bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5 p-6 text-left hover:bg-sky-gray/10 transition-colors">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-rabbit text-lg pr-4">
                      Do you accept new patients?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 1 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFAQ === 1 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5">
                    <p className="text-chocolate-malt">
                      Yes! We're currently accepting new patients. We often have same-day appointments available for emergencies, and we can usually book new patient consultations within 48 hours.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ 2 */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
                  className="w-full bg-gradient-to-r from-sky-gray/5 to-burnt-coral/5 p-6 text-left hover:bg-burnt-coral/10 transition-colors">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-rabbit text-lg pr-4">
                      What if I haven't been to the dentist in years?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 3 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFAQ === 3 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-r from-sky-gray/5 to-burnt-coral/5">
                    <p className="text-chocolate-malt">
                      No judgment here! We understand life gets busy and dental anxiety is real. We specialize in making patients feel comfortable, especially those who haven't visited in a while. We'll take things at your pace.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ 3 */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
                  className="w-full bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5 p-6 text-left hover:bg-sky-gray/10 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-rabbit text-lg pr-4">
                      Do you offer payment plans?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 4 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFAQ === 4 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5">
                    <p className="text-chocolate-malt mb-4">
                      Yes! We offer flexible payment plans to make dental care affordable. We can discuss options during your consultation, including 0% finance for larger treatments like Invisalign.
                    </p>
                    <Link 
                      to="/finance"
                      className="inline-flex items-center gap-2 text-burnt-coral hover:text-golden-sand font-semibold transition-colors"
                    >
                      Calculate your monthly payment
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>

              {/* FAQ 4 */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 4 ? null : 4)}
                  className="w-full bg-gradient-to-r from-sky-gray/5 to-burnt-coral/5 p-6 text-left hover:bg-burnt-coral/10 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-rabbit text-lg pr-4">
                      Is Invisalign really as good as traditional braces?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 4 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFAQ === 4 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-r from-sky-gray/5 to-burnt-coral/5">
                    <p className="text-chocolate-malt">
                      For most cases, yes! Dr. Kartik Patel is an experienced Invisalign provider with 150+ cases completed. During your free consultation, we'll assess if Invisalign is right for you and show you a digital preview of your potential results.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ 5 */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 5 ? null : 5)}
                  className="w-full bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5 p-6 text-left hover:bg-sky-gray/10 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-rabbit text-lg pr-4">
                      What if I'm in pain right now?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 5 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFAQ === 5 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-r from-burnt-coral/5 to-sky-gray/5">
                    <p className="text-chocolate-malt">
                      <strong>Call or WhatsApp us immediately!</strong> We reserve slots specifically for emergency patients and can usually see you the same day. Getting you out of pain is our priority.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ 6: Why is dentistry expensive? */}
              <div className="border-2 border-burnt-coral/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === 6 ? null : 6)}
                  className="w-full bg-gradient-to-r from-sky-gray/5 to-burnt-coral/5 p-6 text-left hover:bg-burnt-coral/10 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-rabbit text-lg md:text-xl pr-4">
                      💡 Why is dentistry expensive?
                    </h4>
                    <ChevronDown 
                      className={`w-6 h-6 text-burnt-coral flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === 6 ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    openFAQ === 6 ? 'max-h-[6000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-6 bg-gradient-to-br from-rabbit/5 to-sky-gray/10">
                    {/* Detailed Explanation - FIRST */}
                    <div className="mb-8 bg-white rounded-xl p-6 md:p-8 shadow-md border-l-4 border-burnt-coral">
                      <h5 className="text-lg md:text-xl font-bold text-rabbit mb-4 flex items-center gap-2">
                        <span className="text-2xl">💰</span>
                        Why We Charge What We Charge
                      </h5>
                      <div className="text-chocolate-malt leading-relaxed space-y-3">
                        <p>
                          At our dental practice, we uphold a commitment to providing the <strong>highest quality dental treatment and care</strong>, which reflects in our fee structure. Our fees are reflective of the superior standard of care we deliver, incorporating the use of top-tier materials and cutting-edge equipment to ensure optimal results for our patients.
                        </p>
                        <p>
                          By investing in <strong>advanced digital technology</strong>, we enhance the precision and efficiency of our procedures, ultimately benefiting our patients' experience and outcomes. Additionally, we collaborate closely with <strong>master dental technicians</strong>, ensuring that each aspect of our patients' treatment meets the highest standards of craftsmanship.
                        </p>
                        <p>
                          Furthermore, our dedication extends beyond the treatment chair, as we provide <strong>excellent aftercare and ongoing support</strong> to our patients, ensuring their continued dental health and satisfaction.
                        </p>
                        <p className="pt-3 border-t border-burnt-coral/20 font-semibold text-rabbit">
                          We believe that the quality of care we provide justifies the fees we charge, as our patients receive not only exceptional treatment but also peace of mind knowing that they are receiving the best possible care available.
                        </p>
                      </div>
                    </div>

                    <p className="text-chocolate-malt mb-6 text-lg">
                      The truth about dental care: <strong>You can only pick TWO</strong> of these three things. Here's why:
                    </p>
                    
                    {/* Triangle Visualization */}
                    <div className="relative mb-12">
                      <svg viewBox="-20 -20 440 390" className="w-full max-w-2xl mx-auto">
                        {/* Triangle Lines */}
                        <line x1="200" y1="50" x2="50" y2="300" stroke="#CB57AC" strokeWidth="3" strokeDasharray="5,5" />
                        <line x1="200" y1="50" x2="350" y2="300" stroke="#CB57AC" strokeWidth="3" strokeDasharray="5,5" />
                        <line x1="50" y1="300" x2="350" y2="300" stroke="#CB57AC" strokeWidth="3" strokeDasharray="5,5" />
                        
                        {/* Center Text */}
                        <text x="200" y="200" textAnchor="middle" fill="#5C5462" fontSize="24" fontWeight="bold">
                          Pick 2
                        </text>
                        
                        {/* Top Point - EXCELLENCE */}
                        <circle cx="200" cy="50" r="65" fill="#CB57AC" />
                        <text x="200" y="57" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                          EXCELLENCE
                        </text>
                        
                        {/* Bottom Left - CHEAP */}
                        <circle cx="50" cy="300" r="65" fill="#15BCCA" />
                        <text x="50" y="307" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                          CHEAP
                        </text>
                        
                        {/* Bottom Right - EFFICIENT */}
                        <circle cx="350" cy="300" r="65" fill="#BD7A0B" />
                        <text x="350" y="307" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                          EFFICIENT
                        </text>
                      </svg>
                    </div>

                    {/* Interactive Options */}
                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                      {/* Option 1: Excellence + Cheap = Not Efficient */}
                      <div className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-200">
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-burnt-coral flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">EXCELLENCE</h5>
                        </div>
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-sky-gray flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">CHEAP</h5>
                        </div>
                        <div className="flex gap-2 mb-4">
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold flex-shrink-0 text-sm">
                            ✗
                          </div>
                          <h5 className="text-base font-bold text-gray-400">NOT Efficient</h5>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm text-chocolate-malt">
                            <strong>Result:</strong> Months-long wait times. Excellence takes time when done affordably.
                          </p>
                        </div>
                      </div>

                      {/* Option 2: Efficient + Excellence = Not Cheap */}
                      <div className="bg-gradient-to-br from-burnt-coral/10 to-sky-gray/10 rounded-xl p-5 shadow-lg border-4 border-burnt-coral">
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-amberlight flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">EFFICIENT</h5>
                        </div>
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-burnt-coral flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">EXCELLENCE</h5>
                        </div>
                        <div className="flex gap-2 mb-4">
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold flex-shrink-0 text-sm">
                            ✗
                          </div>
                          <h5 className="text-base font-bold text-gray-400">NOT Cheap</h5>
                        </div>
                        <div className="pt-4 border-t-2 border-burnt-coral">
                          <p className="text-sm text-chocolate-malt">
                            <strong>This is restore.dental:</strong> Premium quality + fast service = investment in results that last.
                          </p>
                        </div>
                      </div>

                      {/* Option 3: Cheap + Efficient = Not Good */}
                      <div className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-200">
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-sky-gray flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">CHEAP</h5>
                        </div>
                        <div className="flex gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-amberlight flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                            ✓
                          </div>
                          <h5 className="text-base font-bold text-rabbit">EFFICIENT</h5>
                        </div>
                        <div className="flex gap-2 mb-4">
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold flex-shrink-0 text-sm">
                            ✗
                          </div>
                          <h5 className="text-base font-bold text-gray-400">NOT Excellence</h5>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm text-chocolate-malt">
                            <strong>Result:</strong> Work needs redoing soon. You spend more fixing it later.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-amberlight/10 rounded-xl p-6 md:p-8 text-center border-2 border-burnt-coral">
                      <h5 className="text-xl md:text-2xl font-bold text-rabbit mb-3">
                        We Choose Excellence + Efficiency
                      </h5>
                      <p className="text-base text-chocolate-malt mb-3 leading-relaxed">
                        At restore.dental, we prioritize <strong>exceptional results delivered efficiently</strong>. Premium materials, advanced techniques, same-day emergency slots. Yes, it comes at a cost – but we keep it as reasonable as possible using efficient systems.
                      </p>
                      <p className="text-base text-chocolate-malt font-semibold">
                        The difference? Your results last years, not months.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-chocolate-malt mb-4">
                Still have questions?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
                  size="lg"
                  asChild
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Ask on WhatsApp
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
                  asChild
                >
                  <a href={phoneLink}>
                    <Phone className="w-4 h-4 mr-2" />
                    Call Us
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-rabbit via-rabbit/95 to-chocolate-malt text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-burnt-coral rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-sky-gray rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Smile?
            </h3>
            <p className="text-xl mb-8 text-white/90">
              Join 2000+ happy patients who've restored their confidence with us
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                size="lg"
                className="bg-burnt-coral hover:bg-burnt-coral/90 text-white px-8 py-6 text-lg shadow-2xl"
                onClick={handleBooking}
              >
                <Phone className="w-5 h-5 mr-2" />
                Book Your Free Consultation
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-rabbit px-8 py-6 text-lg"
                asChild
              >
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </a>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span>Same-day appointments</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span>No-judgment care</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <Link to="/finance" className="hover:text-burnt-coral transition-colors">
                  Flexible payment plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20 bg-rabbit text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Get In Touch
            </h3>
            <p className="text-lg text-white/80">
              Ready to restore your smile? Contact us today
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors duration-300">
              <MapPin className="w-8 h-8 text-burnt-coral mx-auto mb-3" />
              <h4 className="font-semibold mb-2">Location</h4>
              <p className="text-white/80">West London<br />W3 6NP</p>
            </div>

            <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors duration-300">
              <Phone className="w-8 h-8 text-burnt-coral mx-auto mb-3" />
              <h4 className="font-semibold mb-2">Phone</h4>
              <a href="tel:07787814064" className="text-white/80 hover:text-white">
                07787 814 064
              </a>
            </div>

            <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors duration-300">
              <WhatsAppIcon className="w-8 h-8 text-burnt-coral mx-auto mb-3" />
              <h4 className="font-semibold mb-2">WhatsApp</h4>
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white"
              >
                Chat with us
              </a>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-burnt-coral hover:bg-burnt-coral/90 text-white px-8 py-6 text-lg"
              onClick={handleBooking}
            >
              Book Your Appointment
            </Button>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-burnt-coral" />
                <h3 className="text-2xl md:text-3xl font-bold text-rabbit">
                  Visit Us
                </h3>
              </div>
              <p className="text-xl md:text-2xl font-semibold text-rabbit mb-2">
                Restore.dental @ Perfect Smile Acton
              </p>
              <p className="text-lg text-chocolate-malt">
                64a Horn Lane, W3 6NP
              </p>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-sky-gray/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2481.950891234567!2d-0.27099170810255924!3d51.51103445602675!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNTHCsDMwJzM5LjciTiAwwrAxNicxNS42Ilc!5e0!3m2!1sen!2suk!4v1709502123456!5m2!1sen!2suk&style=feature:all|element:geometry|saturation:-70|lightness:30&style=feature:all|element:labels.text.fill|saturation:-100|lightness:40&style=feature:all|element:labels.text.stroke|visibility:off&style=feature:landscape|element:geometry|saturation:-60|lightness:50&style=feature:poi|element:all|saturation:-70|lightness:40&style=feature:road|element:geometry|saturation:-80|lightness:50&style=feature:road|element:labels|visibility:simplified&style=feature:water|element:geometry|saturation:-50|lightness:70|color:0xd4e4f0"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Restore.dental - Exact Location"
                className="w-full"
              ></iframe>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=51.51103445602675,-0.27099170810255924"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-4 bg-burnt-coral text-white rounded-lg hover:bg-burnt-coral/90 transition-colors duration-300 shadow-lg"
              >
                <MapPin className="w-5 h-5" />
                <span className="font-medium">Get Directions</span>
              </a>
              
              <a
                href="tel:07787814064"
                className="flex items-center justify-center gap-2 p-4 bg-sky-gray text-white rounded-lg hover:bg-sky-gray/90 transition-colors duration-300 shadow-lg"
              >
                <Phone className="w-5 h-5" />
                <span className="font-medium">Call Us</span>
              </a>
              
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-300 shadow-lg"
              >
                <WhatsAppIcon className="w-5 h-5" />
                <span className="font-medium">WhatsApp</span>
              </a>
            </div>

            {/* What3Words Section */}
            <div className="mt-8 bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 rounded-2xl p-6 md:p-8 border-2 border-burnt-coral/20 shadow-lg">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                    <MapPin className="w-6 h-6 text-burnt-coral" />
                    <h3 className="text-xl md:text-2xl font-bold text-rabbit">Find Us with What3Words</h3>
                  </div>
                  <p className="text-chocolate-malt mb-4">
                    The easiest way to find our exact entrance. Perfect for Uber, delivery drivers, or if you're new to the area!
                  </p>
                  <a
                    href="https://what3words.com/fade.kick.fortunate"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-white px-6 py-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-burnt-coral/30 hover:border-burnt-coral group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">///</span>
                      <div className="flex flex-col items-start">
                        <span className="text-xs text-chocolate-malt/70 uppercase tracking-wide">what3words</span>
                        <span className="text-lg md:text-xl font-bold text-burnt-coral group-hover:text-burnt-coral/80 transition-colors">
                          fade.kick.fortunate
                        </span>
                      </div>
                    </div>
                    <svg 
                      className="w-5 h-5 text-burnt-coral group-hover:translate-x-1 transition-transform" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
                
                <div className="flex-shrink-0 text-center">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <div className="text-6xl mb-2">📍</div>
                    <p className="text-sm text-chocolate-malt font-medium">
                      Tap to open in<br />What3Words app
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-chocolate-malt text-white py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Smile className="w-6 h-6 text-burnt-coral" />
              <span className="text-xl font-bold">restore.dental</span>
            </div>
            <p className="text-white/80 mb-4">
              Dr. Kartik Patel | West London Invisalign Specialist
            </p>
            <p className="text-sm text-white/60">
              © 2025 restore.dental. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons - Right Side */}
      <div
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3"
        data-floating-corner="right"
        data-testid="floating-action-buttons"
      >
        {/* Phone Button */}
        <a
          href={phoneLink}
          onClick={trackPhoneClick}
          className="w-14 h-14 bg-burnt-coral hover:bg-burnt-coral/90 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group relative"
          aria-label="Call Us"
        >
          <Phone className="w-7 h-7" />
          <span className="absolute right-16 bg-rabbit text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Call Now
          </span>
        </a>

        {/* WhatsApp Button */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackWhatsAppClick}
          className="w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group relative"
          aria-label="WhatsApp"
        >
          <WhatsAppIcon className="w-7 h-7" />
          <span className="absolute right-16 bg-rabbit text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            WhatsApp
          </span>
        </a>

        {/* Smile Selfie Camera Button */}
        <button
          onClick={() => {
            handleCameraOpen();
            trackSmileSelfieOpen();
          }}
          className="w-14 h-14 bg-sky-gray hover:bg-sky-gray/90 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group relative"
          aria-label="Smile Selfie"
        >
          <Camera className="w-7 h-7" />
          <span className="absolute right-16 bg-rabbit text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Smile Selfie
          </span>
        </button>

        {/* Voice Note Button */}
        <button
          onClick={() => {
            handleVoiceRecorderOpen();
            trackVoiceNoteOpen();
          }}
          className="w-14 h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group relative"
          aria-label="Leave Voice Note"
        >
          <Mic className="w-7 h-7" />
          <span className="absolute right-16 bg-rabbit text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-48">
            On the move? Leave a voice note
          </span>
        </button>
      </div>
      
      {/* Mini Consult NOW Button - Bottom Left */}
      <div className="fixed bottom-6 left-6 z-50">
        <button
          onClick={() => setShowMiniConsult(true)}
          className="relative px-4 py-3 bg-gradient-to-br from-[#01A9B4] to-[#006A71] hover:from-[#006A71] hover:to-[#01A9B4] text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group overflow-hidden"
          aria-label="Mini Consult NOW"
        >
          <span className="relative z-10 text-xs font-bold uppercase tracking-wide whitespace-nowrap">
            mini consult<br />NOW
          </span>
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full bg-[#01A9B4] animate-ping opacity-75"></span>
          {/* Hover tooltip */}
          <span className="absolute left-full ml-3 bg-[#006A71] text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-xl font-semibold">
            Free AI-Powered Assessment
          </span>
        </button>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl">
            {/* Close Button */}
            <button
              onClick={handleCameraClose}
              className="absolute -top-12 right-0 text-white hover:text-burnt-coral transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-burnt-coral to-sky-gray p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">📸 Smile Selfie</h3>
                <p className="text-white/90">Position your smile within the guide and capture!</p>
              </div>

              {!capturedImage ? (
                <div className="relative aspect-[4/3] bg-black">
                  {cameraError ? (
                    <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
                      <div>
                        <Camera className="w-16 h-16 text-burnt-coral mx-auto mb-4" />
                        <p className="text-rabbit mb-4">{cameraError}</p>
                        <Button
                          onClick={startCamera}
                          className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                        >
                          Try Again
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Smile Overlay Guide */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <svg
                          width="300"
                          height="200"
                          viewBox="0 0 300 200"
                          className="drop-shadow-2xl"
                        >
                          {/* Smile outline */}
                          <ellipse
                            cx="150"
                            cy="100"
                            rx="140"
                            ry="90"
                            fill="none"
                            stroke="rgba(203, 87, 172, 0.6)"
                            strokeWidth="3"
                            strokeDasharray="10,5"
                          />
                          {/* Top lip guide */}
                          <path
                            d="M 60 90 Q 150 70 240 90"
                            fill="none"
                            stroke="rgba(21, 188, 202, 0.6)"
                            strokeWidth="2"
                            strokeDasharray="5,3"
                          />
                          {/* Bottom lip guide */}
                          <path
                            d="M 60 110 Q 150 130 240 110"
                            fill="none"
                            stroke="rgba(21, 188, 202, 0.6)"
                            strokeWidth="2"
                            strokeDasharray="5,3"
                          />
                          {/* Smile curve */}
                          <path
                            d="M 80 100 Q 150 120 220 100"
                            fill="none"
                            stroke="rgba(203, 87, 172, 0.8)"
                            strokeWidth="3"
                          />
                          <text
                            x="150"
                            y="30"
                            textAnchor="middle"
                            fill="white"
                            fontSize="16"
                            fontWeight="bold"
                            className="drop-shadow-lg"
                          >
                            Position Your Smile Here
                          </text>
                        </svg>
                      </div>

                      {/* Capture Button */}
                      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                        <button
                          onClick={capturePhoto}
                          className="w-20 h-20 bg-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-200 border-4 border-burnt-coral"
                        >
                          <div className="w-16 h-16 bg-burnt-coral rounded-full"></div>
                        </button>
                      </div>
                    </>
                  )}
                  
                  <canvas ref={canvasRef} className="hidden" />
                </div>
              ) : (
                <div className="relative">
                  <img 
                    src={capturedImage} 
                    alt="Captured smile" 
                    className="w-full aspect-[4/3] object-cover"
                  />
                  
                  <div className="p-6 space-y-4">
                    <p className="text-center text-chocolate-malt">
                      Great smile! Send this to us via WhatsApp to start your smile transformation journey.
                    </p>
                    
                    <div className="flex gap-3">
                      <Button
                        onClick={retakePhoto}
                        variant="outline"
                        className="flex-1 border-2 border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Retake
                      </Button>
                      
                      <Button
                        onClick={sendToWhatsApp}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send to WhatsApp
                      </Button>
                    </div>

                    <p className="text-xs text-center text-chocolate-malt/70">
                      The photo will be downloaded to your device. You can then attach it to the WhatsApp conversation.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voice Recorder Modal */}
      {showVoiceRecorder && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            {/* Close Button */}
            <button
              onClick={handleVoiceRecorderClose}
              className="absolute -top-12 right-0 text-white hover:text-burnt-coral transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">🎤 Leave a Voice Note</h3>
                <p className="text-white/90">Tell us how we can help you!</p>
              </div>

              <div className="p-8">
                {voiceError ? (
                  <div className="text-center py-8">
                    <Mic className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                    <p className="text-rabbit mb-4">{voiceError}</p>
                    <Button
                      onClick={startVoiceRecording}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Try Again
                    </Button>
                  </div>
                ) : !recordedAudio ? (
                  <div className="text-center">
                    {!isRecording ? (
                      <>
                        <div className="mb-6">
                          <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mic className="w-12 h-12 text-purple-600" />
                          </div>
                          <p className="text-rabbit mb-2 font-semibold">Ready to record</p>
                          <p className="text-chocolate-malt/70 text-sm">
                            Tell us about your dental needs, concerns, or questions
                          </p>
                        </div>
                        <Button
                          onClick={startVoiceRecording}
                          size="lg"
                          className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                        >
                          <Mic className="w-5 h-5 mr-2" />
                          Start Recording
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="mb-6">
                          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                            <div className="w-4 h-4 bg-red-600 rounded-full animate-ping"></div>
                          </div>
                          <p className="text-rabbit mb-2 font-semibold">Recording...</p>
                          <p className="text-3xl font-mono text-purple-600 mb-4">
                            {formatTime(recordingTime)}
                          </p>
                          <p className="text-chocolate-malt/70 text-sm">
                            Speak clearly about what you'd like help with
                          </p>
                        </div>
                        <Button
                          onClick={stopVoiceRecording}
                          size="lg"
                          className="bg-red-600 hover:bg-red-700 text-white px-8"
                        >
                          <Square className="w-5 h-5 mr-2" />
                          Stop Recording
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-6">
                      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-12 h-12 text-green-600" />
                      </div>
                      <p className="text-rabbit mb-2 font-semibold">Recording Complete!</p>
                      <p className="text-3xl font-mono text-purple-600 mb-4">
                        {formatTime(recordingTime)}
                      </p>
                    </div>

                    {/* Audio Preview */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <audio
                        src={recordedAudio}
                        controls
                        className="w-full"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={deleteVoiceRecording}
                        variant="outline"
                        className="flex-1 border-2"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Re-record
                      </Button>
                      <Button
                        onClick={sendVoiceNote}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </Button>
                    </div>

                    <p className="text-xs text-center text-chocolate-malt/70 mt-4">
                      The audio will be downloaded to your device. You can then attach it to the WhatsApp conversation.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      {showCookieBanner && (
        <>
          {/* Overlay backdrop */}
          <div className="fixed inset-0 bg-black/60 z-[199] backdrop-blur-sm" data-nosnap></div>
          
          {/* Cookie banner at bottom */}
          <div className="fixed bottom-0 left-0 right-0 z-[200] bg-white border-t-4 border-burnt-coral shadow-2xl animate-slideUp" data-nosnap>
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-bold text-rabbit mb-2 flex items-center gap-2 text-lg">
                    <span>🍪</span> We Value Your Privacy
                  </h4>
                  <p className="text-sm text-chocolate-malt">
                    We use essential cookies to ensure our website works properly. We'd also like to set optional cookies for analytics. Please choose your preference to continue.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    onClick={acceptAllCookies}
                    className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={rejectNonEssential}
                    variant="outline"
                    className="border-2"
                  >
                    Essentials Only
                  </Button>
                  <Button
                    onClick={() => setShowCookieSettings(true)}
                    variant="ghost"
                    className="text-burnt-coral hover:text-burnt-coral/80"
                  >
                    Customize
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <footer className="bg-rabbit text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Contact Info */}
            <div>
              <h4 className="font-bold text-xl mb-4">Restore.Dental</h4>
              <p className="text-white/80 mb-2">Dr. Kartik Patel</p>
              <p className="text-white/80 mb-2">64a Horn Lane</p>
              <p className="text-white/80 mb-2">Acton, London W3 6NP</p>
              <p className="text-white/80 mb-2">📞 07787 814064</p>
              <div className="mt-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors block mb-2">
                  💬 WhatsApp Us
                </a>
                <a href={googleBusinessLink} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors block">
                  ⭐ Google Reviews
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-xl mb-4">Our Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/invisalign-acton" className="text-white/80 hover:text-white transition-colors">
                    Invisalign Acton
                  </Link>
                </li>
                <li>
                  <Link to="/implants-acton" className="text-white/80 hover:text-white transition-colors">
                    Dental Implants
                  </Link>
                </li>
                <li>
                  <Link to="/composite-bonding-acton" className="text-white/80 hover:text-white transition-colors">
                    Composite Bonding
                  </Link>
                </li>
                <li>
                  <Link to="/cosmetic-dentist-acton" className="text-white/80 hover:text-white transition-colors">
                    Cosmetic Dentistry
                  </Link>
                </li>
                <li>
                  <Link to="/dentist-acton" className="text-white/80 hover:text-white transition-colors">
                    General Dentistry
                  </Link>
                </li>
                <li>
                  <Link to="/emergency-dentist-acton" className="text-white/80 hover:text-white transition-colors">
                    Emergency Dentist
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal & Policies */}
            <div>
              <h4 className="font-bold text-xl mb-4">Legal & Policies</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/cancellation-policy"
                    className="text-white/80 hover:text-white transition-colors"
                    data-testid="footer-cancellation-policy"
                  >
                    Cancellation Policy
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => setShowPrivacyPolicy(true)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowCookiePolicy(true)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowTerms(true)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowCookieSettings(true)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Manage Cookie Preferences
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Site-wide crawlable links: every treatment, area and practice page */}
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 border-t border-white/20 pt-10 mb-8" data-testid="home-site-links">
            <SiteLinkColumns headingClass="font-bold text-lg mb-4" />
          </div>
          <div className="border-t border-white/20 pt-10 mb-8">
            <AreaTreatmentLinks headingClass="font-bold text-lg mb-4" />
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/20 pt-6 text-center text-white/70 text-sm">
            <p>© {new Date().getFullYear()} Restore.Dental. All rights reserved.</p>
            <p className="mt-2">GDC Registration: 177993</p>
          </div>
        </div>
      </footer>

      {/* Cookie Settings Modal */}
      {showCookieSettings && (
        <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-burnt-coral to-sky-gray p-6 text-white">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Cookie Preferences</h3>
                <button onClick={() => setShowCookieSettings(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Essential Cookies */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-bold text-rabbit">Essential Cookies</h4>
                    <p className="text-sm text-chocolate-malt mt-1">
                      Required for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <span className="text-green-600 font-semibold">Always On</span>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-bold text-rabbit">Functional Cookies</h4>
                    <p className="text-sm text-chocolate-malt mt-1">
                      Used for Google Maps to show our location. Helps you find us easily.
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="functional"
                      defaultChecked={cookieConsent?.functional ?? true}
                      className="w-5 h-5 text-burnt-coral rounded"
                    />
                  </label>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-bold text-rabbit">Analytics Cookies</h4>
                    <p className="text-sm text-chocolate-malt mt-1">
                      Help us understand how visitors use our website. Includes Google Analytics to track page views, popular services, and user behavior.
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="analytics"
                      defaultChecked={cookieConsent?.analytics ?? false}
                      className="w-5 h-5 text-burnt-coral rounded"
                    />
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-bold text-rabbit">Marketing Cookies</h4>
                    <p className="text-sm text-chocolate-malt mt-1">
                      Used to show you relevant ads and measure ad effectiveness. Includes Facebook Pixel and Google Ads conversion tracking.
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="marketing"
                      defaultChecked={cookieConsent?.marketing ?? false}
                      className="w-5 h-5 text-burnt-coral rounded"
                    />
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={() => {
                  const functional = document.getElementById('functional').checked;
                  const analytics = document.getElementById('analytics').checked;
                  const marketing = document.getElementById('marketing').checked;
                  saveCustomPreferences({ functional, analytics, marketing });
                }}
                className="w-full bg-burnt-coral hover:bg-burnt-coral/90 text-white"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
            <div className="sticky top-0 bg-gradient-to-r from-burnt-coral to-sky-gray p-6 text-white rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Privacy Policy</h3>
                <button onClick={() => setShowPrivacyPolicy(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto space-y-6 text-sm">
              <p className="text-chocolate-malt"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB')}</p>
              
              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">1. Who We Are</h4>
                <p className="text-chocolate-malt">
                  Restore.Dental is a dental practice operated by Dr. Kartik Patel (GDC: 177993), located at 64a Horn Lane, Acton, London W3 6NP. We are committed to protecting your personal data and respecting your privacy.
                </p>
                <p className="text-chocolate-malt mt-2">
                  <strong>ICO Registration:</strong> We are in the process of registering with the Information Commissioner's Office (ICO).
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">2. Data We Collect</h4>
                <p className="text-chocolate-malt mb-2">We may collect the following information:</p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>Name, email address, and phone number (when you contact us)</li>
                  <li>Dental health information (during consultations)</li>
                  <li>Communication preferences</li>
                  <li>Cookie data (see our Cookie Policy)</li>
                  <li>Photos/images (if you use our Smile Selfie feature)</li>
                  <li>Voice recordings (if you use our Voice Note feature)</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">3. How We Use Your Data</h4>
                <p className="text-chocolate-malt mb-2">We use your information to:</p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>Provide dental services and treatment</li>
                  <li>Communicate with you about appointments</li>
                  <li>Respond to your inquiries</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Improve our services</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">4. Legal Basis for Processing</h4>
                <p className="text-chocolate-malt mb-2">We process your data under:</p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li><strong>Consent:</strong> When you provide explicit consent</li>
                  <li><strong>Contract:</strong> To fulfill our dental services</li>
                  <li><strong>Legal Obligation:</strong> GDC and NHS requirements</li>
                  <li><strong>Legitimate Interest:</strong> To improve our services</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">5. Data Retention</h4>
                <p className="text-chocolate-malt">
                  We retain patient records for a minimum of 11 years (adults) or until the patient's 25th birthday (children), in accordance with GDC guidelines. Marketing data is retained until you withdraw consent.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">6. Your Rights</h4>
                <p className="text-chocolate-malt mb-2">Under GDPR and UK DPA 2018, you have the right to:</p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>Access your personal data</li>
                  <li>Rectify inaccurate data</li>
                  <li>Request deletion ("right to be forgotten")</li>
                  <li>Restrict processing</li>
                  <li>Data portability</li>
                  <li>Object to processing</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">7. Third Parties</h4>
                <p className="text-chocolate-malt mb-2">We may share data with:</p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>NHS and GDC (regulatory compliance)</li>
                  <li>Google (Maps, Reviews API - see Cookie Policy)</li>
                  <li>WhatsApp (when you initiate contact via our buttons)</li>
                  <li>Professional insurers and legal advisors</li>
                </ul>
                <p className="text-chocolate-malt mt-2">We do not sell your data to third parties.</p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">8. Data Security</h4>
                <p className="text-chocolate-malt">
                  We implement appropriate technical and organizational measures to protect your data, including encryption, secure servers, and access controls.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">9. Contact Us</h4>
                <p className="text-chocolate-malt">
                  For any privacy concerns or to exercise your rights, contact us at:<br />
                  📞 07787 814064<br />
                  📍 64a Horn Lane, Acton, London W3 6NP
                </p>
                <p className="text-chocolate-malt mt-2">
                  You also have the right to lodge a complaint with the ICO: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-burnt-coral hover:underline">www.ico.org.uk</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Policy Modal */}
      {showCookiePolicy && (
        <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
            <div className="sticky top-0 bg-gradient-to-r from-burnt-coral to-sky-gray p-6 text-white rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Cookie Policy</h3>
                <button onClick={() => setShowCookiePolicy(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto space-y-6 text-sm">
              <p className="text-chocolate-malt"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB')}</p>
              
              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">What Are Cookies?</h4>
                <p className="text-chocolate-malt">
                  Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience and understand how you use our site.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">Cookies We Use</h4>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h5 className="font-bold text-rabbit">Essential Cookies (Always Active)</h5>
                    <p className="text-chocolate-malt mt-1">
                      These cookies are necessary for the website to function and cannot be switched off.
                    </p>
                    <ul className="list-disc pl-6 text-chocolate-malt mt-2 space-y-1">
                      <li><strong>cookieConsent:</strong> Stores your cookie preferences</li>
                      <li><strong>Duration:</strong> 1 year</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-blue-500 pl-4">
                    <h5 className="font-bold text-rabbit">Functional Cookies (Optional)</h5>
                    <p className="text-chocolate-malt mt-1">
                      These cookies enable enhanced functionality and personalization.
                    </p>
                    <ul className="list-disc pl-6 text-chocolate-malt mt-2 space-y-1">
                      <li><strong>Google Maps:</strong> Displays our practice location on the map</li>
                      <li><strong>Provider:</strong> Google LLC</li>
                      <li><strong>Duration:</strong> Session-based</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">Managing Cookies</h4>
                <p className="text-chocolate-malt mb-2">
                  You can control cookies through:
                </p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>Our cookie banner (appears on first visit)</li>
                  <li>Cookie Settings in the footer</li>
                  <li>Your browser settings</li>
                </ul>
                <p className="text-chocolate-malt mt-2">
                  Note: Disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">Third-Party Cookies</h4>
                <p className="text-chocolate-malt">
                  We use Google Maps to display our location. Google may set cookies for this service. Please see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-burnt-coral hover:underline">Google's Privacy Policy</a> for more information.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">Updates to This Policy</h4>
                <p className="text-chocolate-malt">
                  We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated revision date.
                </p>
              </section>

              <div className="pt-4 border-t">
                <Button
                  onClick={() => {
                    setShowCookiePolicy(false);
                    setShowCookieSettings(true);
                  }}
                  className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
                >
                  Manage Cookie Preferences
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTerms && (
        <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
            <div className="sticky top-0 bg-gradient-to-r from-burnt-coral to-sky-gray p-6 text-white rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Terms of Service</h3>
                <button onClick={() => setShowTerms(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto space-y-6 text-sm">
              <p className="text-chocolate-malt"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB')}</p>
              
              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">1. Acceptance of Terms</h4>
                <p className="text-chocolate-malt">
                  By accessing and using this website, you accept and agree to be bound by these Terms of Service and our Privacy Policy.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">2. Services</h4>
                <p className="text-chocolate-malt">
                  This website provides information about our dental services. It does not constitute medical advice. Always consult with a qualified dental professional for specific dental concerns.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">3. Appointments</h4>
                <p className="text-chocolate-malt">
                  Appointment requests made through this website are subject to confirmation. We will contact you to confirm your appointment details.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">4. User Content</h4>
                <p className="text-chocolate-malt mb-2">
                  When you submit photos (Smile Selfie) or voice notes through our website:
                </p>
                <ul className="list-disc pl-6 text-chocolate-malt space-y-1">
                  <li>You retain ownership of your content</li>
                  <li>You grant us permission to use it for consultation purposes</li>
                  <li>We will not share your content publicly without explicit consent</li>
                  <li>You confirm you have the right to share the content</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">5. Intellectual Property</h4>
                <p className="text-chocolate-malt">
                  All content on this website, including text, graphics, logos, and images, is the property of Restore.Dental and protected by copyright laws.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">6. Limitation of Liability</h4>
                <p className="text-chocolate-malt">
                  We strive for accuracy but do not guarantee that information on this website is complete, accurate, or up-to-date. We are not liable for any damages arising from use of this website.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">7. Professional Standards</h4>
                <p className="text-chocolate-malt">
                  Dr. Kartik Patel is registered with the General Dental Council (GDC: 177993) and adheres to GDC standards and guidelines.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">8. Governing Law</h4>
                <p className="text-chocolate-malt">
                  These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">9. Changes to Terms</h4>
                <p className="text-chocolate-malt">
                  We reserve the right to modify these terms at any time. Continued use of the website after changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section>
                <h4 className="font-bold text-rabbit text-lg mb-2">10. Contact Information</h4>
                <p className="text-chocolate-malt">
                  For questions about these terms, contact us at:<br />
                  📞 07787 814064<br />
                  📍 64a Horn Lane, Acton, London W3 6NP
                </p>
              </section>
            </div>
          </div>
        </div>
      )}
      
      {/* Mini Consult Modal */}
      <MiniConsultModal isOpen={showMiniConsult} onClose={() => setShowMiniConsult(false)} />
      <BookingModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} serviceName={selectedService} />
    </div>
  );
};

export default Home;